import type { MutationTree, GetterTree } from 'vuex'
import RootState from '@web/store/types'
import type { TagState, Tag } from '@club/types/Tag'
import type { Category } from '@club/types/Category'
import { translit } from '@web/common/translit'
import Logger from '@web/common/Logger'

export enum tagType {
  reset = 'reset',
  favourite = 'favourite',
  category = 'category'
}

// title — полный i18n-ключ (ключи лежат в секции club.*), переводится в cTagsCloud.
const tagAll: Tag = {
  id: 'all',
  title: 'club.All',
  slug: 'all',
  type: tagType.reset,
  isSelected: false,
  translatable: true
}

const tagFavourite: Tag = {
  id: 'favourite',
  title: 'club.Favourites',
  slug: 'favourite',
  type: tagType.favourite,
  isSelected: false,
  translatable: true
}

function generateCategoryTagId (id: number | string): string {
  return `${tagType.category}_${id}`
}

const state: TagState = {
  selectedTagIds: new Set([tagAll.id])
}

// Признак включённости вкладки «Избранное» в фильтрах ленты.
// В исходнике (Nuxt) брался из useRuntimeConfig().public.FAVOURITE_ENABLED.
// NEEDS_CONTEXT: у нас пока нет ExternalConfig-геттера/env для этого клубного флага —
// используем process.env.VUE_APP_CLUB_FAVOURITE_ENABLED по аналогии с club/api/utils.ts,
// но переменная ещё не заведена в envs/.env.*. Уточнить источник флага перед релизом раздела.
function isFavouriteEnabled (): boolean {
  return process.env.VUE_APP_CLUB_FAVOURITE_ENABLED === 'true'
}

const getters: GetterTree<TagState, RootState> = {
  tags: ({ selectedTagIds }, getters, rootState, rootGetters) => {
    const tags: Tag[] = []
    tags.push({
      ...tagAll,
      isSelected: selectedTagIds.has(tagAll.id)
    })

    if (isFavouriteEnabled()) {
      tags.push({
        ...tagFavourite,
        isSelected: selectedTagIds.has(tagFavourite.id)
      })
    }
    const categories: Category[] = rootGetters['clubCategories/categoriesByClubId']
      .filter((category: Category) => category.show_in_filters)

    for (const category of categories) {
      tags.push({
        id: category.id,
        title: category.title,
        slug: category.slug || translit(category.title),
        type: tagType.category,
        isSelected: selectedTagIds.has(generateCategoryTagId(category.id))
      })
    }

    return tags
  },
  getTagAll: () => tagAll,
  getTagFavourite: () => tagFavourite,
  getSelectedTags: (state, { tags }): Tag[] => tags.filter((tag: Tag) => tag.isSelected)
}

const mutations: MutationTree<TagState> = {
  selectTag (state: TagState, tag: Tag) {
    let selectedTagIds = state.selectedTagIds
    function reset (): void {
      selectedTagIds = new Set([tagAll.id])
    }

    function favourite (): void {
      selectedTagIds = new Set([tagFavourite.id])
    }

    function selectCategory (id: number | string): void {
      const categoryTagId = generateCategoryTagId(id)

      if (selectedTagIds.has(categoryTagId)) {
        reset()
      } else {
        selectedTagIds = new Set([categoryTagId])
      }
    }

    switch (tag.type) {
      case tagType.reset:
        reset()
        break
      case tagType.favourite:
        favourite()
        break
      case tagType.category:
        selectCategory(tag.id)
        break
      default:
        Logger.error(`Unsupported tag type. Type: ${tag.type}`)
    }

    state.selectedTagIds = selectedTagIds
  },

  logout (state: TagState) {
    state.selectedTagIds = new Set()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations
}
